import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { formatCnpj } from '@/shared/format/cnpj';
import { assignSchema as sharedAssignSchema, type AssignMessages, type AssignValues as SharedAssignValues } from '@/shared/record';

import type { ManualUpdateBody, RegisteredReserveToken, ReserveTokenCreate } from '../credit-note.wire';
import type { NoteCtx } from './credit-note-actions';
import type { ManualUpdateAction } from './bar-options';

// Os corpos do fio das ações da barra, a partir dos valores de tela (funções puras, com o contrato
// por chamada em credit-note-bar.contract.test.ts). Cada uma cita o componente do legado que monta o corpo.

/** Um token do "Adicionar token consignado" (`ReserveToken.tsx`): sistema, tipo, valor e expiração (data civil). */
export interface TokenRow {
  warrantyRegistrationOffice: string | null;
  tokenType: string | null;
  tokenValue: string | null;
  expirationDate: string | null;
}

export interface ShareValues {
  principalType: string | null;
  principalId: string | null;
  permissionLevelType: string | null;
}

/** Os valores da peça de `shared/record`; o seletor remoto do correspondente limpa com `null`. */
export type AssignValues = Omit<SharedAssignValues, 'tenant'> & { tenant: string | null };

const ASSIGN_MESSAGES: AssignMessages = {
  user: msg`Operador: precisa ser preenchido.`,
  group: msg`Grupo: precisa ser preenchido.`,
  tenant: msg`Tenant: precisa ser preenchido.`,
};

/** AssignSchema.ts:3-22 pela peça de `shared/record`: correspondente `null` vale como vazio (U50). */
export const assignSchema = (i18n: I18n) =>
  z.preprocess(
    (values) => ({ ...(values as AssignValues), tenant: (values as AssignValues).tenant ?? '' }),
    sharedAssignSchema(i18n, ASSIGN_MESSAGES),
  ) as unknown as z.ZodType<AssignValues, AssignValues>;

/** Data civil → o `toIsoStringWithTimezone` da meia-noite (helpers/formats/DateFormat.ts:218-233), sem cast do yup. */
export const civilToLegacyOffset = (day: string | null | undefined) => (day ? `${day}T00:00:00.000-03:00` : null);
/**
 * Data civil com a hora de `at` em -03:00, em ISO UTC: o seletor do legado nasce com `new Date()` e,
 * ao trocar o dia, guarda a hora do valor (DatePickerFormField.tsx:111-120; o yup devolve `Date`).
 */
export const civilWithTimeOf = (day: string | null | undefined, at: Date) => {
  if (!day) return null;
  const time = new Date(at.getTime() - 3 * 3_600_000).toISOString().slice(11, 23);
  return new Date(`${day}T${time}-03:00`).toISOString();
};

/** Data civil → o `Date` do yup (`date()`), em ISO UTC: a meia-noite de -03:00. */
export const civilToYupDate = (day: string | null | undefined) => (day ? new Date(`${day}T00:00:00-03:00`).toISOString() : null);
/** Corpo do "Adicionar token consignado" (ReserveTokenContainer.tsx:64-80): o yup converte a data em `Date` (UTC). */
export function reserveTokenBody(rows: readonly TokenRow[]): ReserveTokenCreate[] {
  return rows.map((row) => ({
    warrantyRegistrationOffice: row.warrantyRegistrationOffice ?? '',
    token: { tokenType: row.tokenType ?? '', tokenValue: row.tokenValue ?? '' },
    expirationDate: civilToYupDate(row.expirationDate) ?? '',
  }));
}

/** O schema das linhas de token (ReserveTokenSchema.ts:4-19): a expiração depois de hoje. */
export function tokenRowsSchema(i18n: I18n, today: string) {
  return z.array(
    z.object({
      warrantyRegistrationOffice: z.string({ message: i18n._(msg`Sistema de averbação: precisa ser preenchido.`) }).min(1, i18n._(msg`Sistema de averbação: precisa ser preenchido.`)),
      tokenType: z.string({ message: i18n._(msg`Tipo do token: precisa ser preenchido.`) }).min(1, i18n._(msg`Tipo do token: precisa ser preenchido.`)),
      tokenValue: z.string({ message: i18n._(msg`Token: precisa ser preenchido.`) }).min(1, i18n._(msg`Token: precisa ser preenchido.`)),
      expirationDate: z
        .string({ message: i18n._(msg`Data de expiração: precisa ser preenchida.`) })
        .refine((day) => day > today, i18n._(msg`Data de expiração: não pode ser menor ou igual à data atual.`)),
    }),
  );
}


/** Rótulo do token no seletor (`useGetAvailableReserveTokenByPersonId`, naturalPersonContext.tsx:255-257). */
export function tokenOptionLabel(token: RegisteredReserveToken) {
  return `${token.result.WarrantyRegistrationOfficeDisplay ?? ''} - ${token.result.Token.TokenTypeDisplay ?? ''} - ${token.result.Token.TokenValue}`;
}

/** Os tokens escolhidos na ordem da lista da API, cada um com o código da mesma posição da escolha (legado). */
export function pickTokens(tokens: readonly RegisteredReserveToken[], selected: readonly string[], codes: readonly (string | undefined)[]) {
  return tokens.filter((token) => selected.includes(token.result.Token.TokenValue)).map((token, index) => ({ token, code: codes[index] }));
}

// ─── valores dos formulários ────────────────────────────────────────────────

export interface ManualUpdateValues extends ShareValues {
  newStatus: string;
  timelineAction: ManualUpdateAction;
  newTimelineType: string | null;
  newTimelineDescription: string;
  workflowAction: ManualUpdateAction;
  acceptanceDate: string | null;
  sendWebCred: boolean;
}
export interface ClosureValues {
  liquidationClosureStatus: string;
  reason: string;
}
export interface TokenPickValues {
  selectedTokens: string[];
  /** O código ADE de cada escolhido, pela posição da escolha; campo nunca digitado fica `undefined`. */
  warrantyCodes: (string | undefined)[];
}
export interface MarginApproveValues extends TokenPickValues {
  message: string;
  valueMarginReserve: number | null;
  includePaymentFixedCosts: boolean;
}
export interface MarginDisapproveValues {
  message: string;
  valueMarginReserve: number | null;
  includePaymentFixedCosts: boolean;
}
export interface DoneRevisionValues {
  reason: string;
  observations: string | null;
  warrantyIds: string[];
  details: TokenRow[];
}
export interface LinkEmployerValues {
  employerName: string;
  employerRegistrationNumber: string;
  includeWebCred: boolean;
}

/** `valueMarginReserve` vai como texto dos centavos: o `string()` do yup converte o número (S12). */
export const marginText = (cents: number | null) => (cents === null ? null : String(cents));

/** `IGetTokensQuery` como o legado monta (`...result, createdAt, createdBy, status, option`), para o `filteredByTokensSelected`. */
export function legacyTokenQuery(token: RegisteredReserveToken) {
  return {
    ...token.result,
    createdAt: token.createdAt,
    createdBy: token.createdBy,
    status: token.status,
    option: { label: tokenOptionLabel(token), value: token.result.Token.TokenValue },
  };
}

/** Corpo do `approveMarginReserve` (ApproveMarginReverseContainer.tsx:40-46 e o efeito do ApproveMarginReverseForm.tsx:32-57). */
export function marginApproveBody(values: MarginApproveValues, tokens: readonly RegisteredReserveToken[]) {
  const picked = pickTokens(tokens, values.selectedTokens, values.warrantyCodes);
  return {
    message: values.message,
    valueMarginReserve: marginText(values.valueMarginReserve),
    includePaymentFixedCosts: values.includePaymentFixedCosts,
    filteredByTokensSelected: picked.map(({ token }) => legacyTokenQuery(token)),
    tokens: picked.map(({ token, code }) => ({
      WarrantyRegistrationOffice: token.result.WarrantyRegistrationOffice,
      token: { tokenType: token.result.Token.TokenType, tokenValue: token.result.Token.TokenValue },
      ExpirationDate: token.result.ExpirationDate,
      ...(code === undefined ? {} : { warrantyCode: code }),
    })),
  };
}

/** Corpo do `approveWarranty` (CompleteManualWarrantyContainer.tsx:40-58); sem campos exigidos, `[]`. */
export function approveWarrantyBody(values: TokenPickValues, tokens: readonly RegisteredReserveToken[]) {
  return pickTokens(tokens, values.selectedTokens, values.warrantyCodes).map(({ token, code }) => ({
    token: { tokenType: token.result.Token.TokenType ?? '', tokenValue: token.result.Token.TokenValue },
    warrantyRegistrationOffice: token.result.WarrantyRegistrationOffice,
    expirationDate: token.result.ExpirationDate,
    ...(code === undefined ? {} : { warrantyCode: code }),
  }));
}

/**
 * Valor da opção de uma garantia sem `id`. O legado usa `item.id` (DoneRevisionForm.tsx:36), e o
 * `undefined` sai como `null` no JSON do array: o corpo manda `null` no lugar (R4 da !11138).
 */
export const WARRANTY_WITHOUT_ID = 'sem-id:';

/** Corpo do `doneWarrantyRevision` (DoneRevisionDrawer.tsx:55-75): tokens só fora do FGTS e com todos preenchidos. */
export function doneRevisionBody(values: DoneRevisionValues, fgts: boolean) {
  const complete = values.details.every((row) => row.tokenValue !== null);
  const mapped = values.details.map((row) => ({
    warrantyRegistrationOffice: row.warrantyRegistrationOffice,
    token: { tokenType: row.tokenType, tokenValue: row.tokenValue },
    expirationDate: civilToLegacyOffset(row.expirationDate),
  }));
  return {
    reason: values.reason,
    observations: values.observations,
    ...(values.warrantyIds.length > 0 ? { warrantyIds: values.warrantyIds.map((id) => (id.startsWith(WARRANTY_WITHOUT_ID) ? null : id)) } : {}),
    warrantyOfficeTokens: !fgts && complete ? mapped : null,
  };
}

/** `carne-{n}-{AAAAMMDD}.pdf` (creditNoteActions.tsx:924-930). */
export const carneFileName = (creditNoteNo: number, today: string) => `carne-${String(creditNoteNo)}-${today.replaceAll('-', '')}.pdf`;

/** Garantia manual sem averbadora: o "Aprovar garantia" só confirma (onApproveWarranty, creditNoteActions.tsx:1263-1281). */
export const manualWithoutOffice = (ctx: NoteCtx) => (ctx.product?.warrantyRegistrationOffice ?? '').toLowerCase() === 'none' && ctx.product?.manualWarranty === true;

export function addShareIssues(values: ShareValues, context: z.RefinementCtx, i18n: I18n) {
  if (!values.principalType) context.addIssue({ code: 'custom', path: ['principalType'], message: i18n._(msg`Tipo: precisa ser selecionado.`) });
  if (!values.principalId) context.addIssue({ code: 'custom', path: ['principalId'], message: i18n._(msg`Registro: precisa ser selecionado.`) });
  if (!values.permissionLevelType) context.addIssue({ code: 'custom', path: ['permissionLevelType'], message: i18n._(msg`Nível de permissão: precisa ser selecionado.`) });
}

/** `shareRecordValidationSchema` (ShareRecordSchema.ts:3-9). */
export function shareSchema(i18n: I18n) {
  return z
    .object({ principalType: z.string().nullable(), principalId: z.string().nullable(), permissionLevelType: z.string().nullable() })
    .superRefine((values, context) => addShareIssues(values, context, i18n));
}

/** Corpo do `manualUpdate` (ManualUpdate.tsx:27-36): `sendWebCred` sai do corpo; campo nunca preenchido não vai. */
export function manualUpdateBody(values: ManualUpdateValues, at: Date): ManualUpdateBody {
  return {
    newStatus: values.newStatus,
    timelineAction: values.timelineAction,
    ...(values.newTimelineType ? { newTimelineType: values.newTimelineType } : {}),
    ...(values.newTimelineDescription ? { newTimelineDescription: values.newTimelineDescription } : {}),
    workflowAction: values.workflowAction,
    acceptanceDate: civilWithTimeOf(values.acceptanceDate, at),
  };
}

/** Corpo do "Definir status roteiro" (LiquidationScheduleClosureUpdateStatus/index.tsx:30-35): número e motivo sem espaços nas pontas. */
export function closureBody(values: ClosureValues) {
  return { liquidationClosureStatus: Number(values.liquidationClosureStatus) as 0 | 5 | 6, reason: values.reason.trim() };
}

/** Corpo do "Associar empregador" (LinkEmployerContainer.tsx:43-66): o CNPJ com a máscara do campo (U21). */
export function linkEmployerBody(values: LinkEmployerValues, creditNoteNo: number) {
  return {
    includeWebCred: values.includeWebCred,
    ops: [{ employerName: values.employerName, employerRegistrationNumber: formatCnpj(values.employerRegistrationNumber) ?? values.employerRegistrationNumber, creditNoteNo }],
  };
}

/** Corpo do "Atribuir" (AssignDrawer.tsx:64-72): o correspondente só quando escolhido. */
export function assignBody(values: Pick<AssignValues, 'tenant' | 'userId' | 'groupName'>) {
  return { userId: values.userId, groupName: values.groupName, ...(values.tenant ? { tenant: values.tenant } : {}) };
}
